/**
 * アプリケーションのルーティング設定
 * クリニック側とカスタマー側の両方のルートを管理
 * Vue Routerを使用してSPAのルーティングを実装
 */

/**
 * 外部ライブラリのインポート
 * - @vueuse/integrations/useCookies: Cookie管理用
 * - vue-router: Vue.jsのルーティング機能
 */
import { useCookies } from "@vueuse/integrations/useCookies";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

/**
 * クリニック側の認証関連コンポーネント
 * - SignIn: ログイン画面
 * - ForgotPassword: パスワード忘れ画面
 * - ResetPassword: パスワードリセット画面
 */
import SignIn from "@/clinic/views/SignIn/index.vue";
import ForgotPassword from "@/clinic/views/ForgotPassword/index.vue";
import ResetPassword from "@/clinic/views/ResetPassword/index.vue";

/**
 * クリニック側のメイン機能コンポーネント
 * - Dashboard: ダッシュボード画面
 * - Campaign: キャンペーン管理画面
 * - Booking: 予約管理画面
 */
import Dashboard from "@/clinic/views/Dashboard.vue";
import Campaign from "@/clinic/views/Campaign.vue";
import CampaignNew from "@/clinic/views/Campaign/new/index.vue";
import CampaignResult from "@/clinic/views/CampaignResult.vue";
import MainContent from "@/clinic/views/MainContent.vue";
import ClinicRouterConstants from "@/clinic/constants/router.constants";
import Booking from "@/clinic/views/Booking/index.vue";
import CampaignNodata from "@/clinic/views/CampaignNodata.vue";

/**
 * クリニック側の設定・管理コンポーネント
 * - ClinicRoutine: クリニックの日常業務管理
 * - Contact: お問い合わせ管理
 * - Customer: 顧客管理
 * - Setting: 設定画面
 */
import ClinicRoutine from "@/clinic/views/ClinicRoutine/index.vue";
import Contact from "@/clinic/views/Contact/index.vue";
import Customer from "@/clinic/views/Customer/index.vue";
import CustomerNew from "@/clinic/views/Customer/new.vue";
import CustomerEdit from "@/clinic/views/Customer/edit.vue";
import Setting from "@/clinic/views/Setting.vue";

/**
 * クリニック側のメッセージング関連コンポーネント
 * - SegmentWorkflow: セグメントワークフロー
 * - RichMenu: リッチメニュー管理
 * - Message: メッセージ管理
 */
import SegmentWorkflow from "@/clinic/views/SegmentWorkflow.vue";
import RichMenu from "@/clinic/views/RichMenu.vue";
import RichMenuRegistration from "@/clinic/views/RichMenuRegistration.vue";
import RichMenuEdit from "@/clinic/views/RichMenuEdit.vue";
import MessageNew from "@/clinic/views/ScheduledMessage/new.vue";
import MessageEdit from "@/clinic/views/ScheduledMessage/edit.vue";
import MessageList from "@/clinic/views/ScheduledMessage/index.vue";
import DirectMessage from "@/clinic/views/DirectMessage/index.vue";

/**
 * クリニック側のスタッフ管理コンポーネント
 * - StaffNew: スタッフ新規登録
 * - StaffList: スタッフ一覧
 * - StaffEdit: スタッフ編集
 * - StaffShift: スタッフシフト管理
 */
import StaffNew from "@/clinic/views/Staffs/new.vue";
import StaffList from "@/clinic/views/Staffs/index.vue";
import StaffEdit from "@/clinic/views/Staffs/edit.vue";
import ClinicSettingsStaffShift from "@/clinic/views/ClinicSettings/StaffShift/index.vue";

/**
 * クリニック側のメニュー管理コンポーネント
 * - MedicalMenu: 医療メニュー管理
 * - BookingSettings: 予約設定
 * - MedicalOptions: 医療オプション管理
 */
import NewMedicalMenu from "@/clinic/views/MedicalMenu/new.vue";
import EditingMedicalMenu from "@/clinic/views/MedicalMenu/edit.vue";
import MedicalMenuList from "@/clinic/views/MedicalMenu/index.vue";
import BookingSettingsList from "@/clinic/views/BookingSetting.vue";
import MedicalOptions from "@/clinic/views/MedicalOptions/index.vue";
import MedicalOptionNew from "@/clinic/views/MedicalOptions/Create.vue";
import MedicalOptionEdit from "@/clinic/views/MedicalOptions/Edit.vue";
import MedicalMenuCategories from "@/clinic/views/MedicalMenuCategories/index.vue";

/**
 * クリニック側のメッセージテンプレート管理コンポーネント
 * - LiveMessageTemplates: ライブメッセージテンプレート
 */
import LiveMessageTemplates from "@/clinic/views/LiveMessageTemplates/index.vue";
import LiveMessageTemplateNew from "@/clinic/views/LiveMessageTemplates/new.vue";
import LiveMessageTemplateEdit from "@/clinic/views/LiveMessageTemplates/edit.vue";

/**
 * クリニック側の顧客管理関連コンポーネント
 * - ClientDetails: 顧客詳細
 * - LineNotificationMessage: LINE通知メッセージ
 */
import ClientDetailsList from "@/clinic/views/ClientDetail.vue";
import LineNotificationMessage from "@/clinic/views/LineNotificationMessage/index.vue";
import LineNotificationMessageNew from "@/clinic/views/LineNotificationMessage/new.vue";
import LineNotificationMessageEdit from "@/clinic/views/LineNotificationMessage/edit.vue";

/**
 * クリニック側の配信管理コンポーネント
 * - ServiceTag: サービスタグ管理
 * - DistributionGroup: 配信グループ管理
 */
import ServiceTag from "@/clinic/views/ServiceTags/index.vue";
import SegmentManualTags from "@/clinic/views/SegmentManualTags/index.vue";
import DistributionGroup from "@/clinic/views/DistributionGroup/index.vue";
import TaggedDelivery from "@/clinic/views/DistributionGroup/TaggedDeliverySummary.vue";
import NewDistributionGroup from "@/clinic/views/DistributionGroup/new.vue";
import EditDistributionGroup from "@/clinic/views/DistributionGroup/edit.vue";
import DistributionGroupDrafts from "@/clinic/views/DistributionGroup/drafts.vue";
import TargetCustomers from "@/clinic/views/DistributionGroup/TargetCustomers.vue";
import DuplicateDistributionGroup from "@/clinic/views/DistributionGroup/Duplicate.vue";

/**
 * クリニック側の定期配信管理コンポーネント
 * - RoutineDistributionGroup: 定期配信グループ管理
 */
import RoutineDistributionGroup from "@/clinic/views/DistributionGroupRoutine/index.vue";
import NewRoutineDistributionGroup from "@/clinic/views/DistributionGroupRoutine/new.vue";
import EditRoutineDistributionGroup from "@/clinic/views/DistributionGroupRoutine/edit.vue";
import DistributionRoutineGroupDrafts from "@/clinic/views/DistributionGroupRoutine/drafts.vue";
import DuplicateRoutineDistributionGroup from "@/clinic/views/DistributionGroupRoutine/Duplicate.vue";

/**
 * クリニック側のその他管理コンポーネント
 * - PrivacyPolicy: プライバシーポリシー
 * - TermsOfService: 利用規約
 * - CompanyProfile: 会社概要
 */
import PrivacyPolicy from "@/clinic/views/static/PrivacyPolicy.vue";
import TermsOfService from "@/clinic/views/static/TermsOfService.vue";
import CompanyProfile from "@/clinic/views/static/CompanyProfile.vue";

/**
 * クリニック側の予約・スタッフ管理コンポーネント
 * - Rooms: 部屋管理
 * - BookingNotifications: 予約通知管理
 * - StaffPriority: スタッフ優先順位管理
 */
import Rooms from "@/clinic/views/Rooms/index.vue";
import BookingNotifications from "@/clinic/views/BookingNotifications/index.vue";
import StaffPriority from "@/clinic/views/StaffPriority/index.vue";
import DuplicateCustomer from "@/clinic/views/DuplicateCustomer/index.vue";

/**
 * カスタマー側のコンポーネント
 * - CustomerMainContent: カスタマー用メインコンテンツ
 * - CustomerBookingWrapper: 予約フローラッパー
 * - BookingHistory: 予約履歴
 */
import CustomerRouterConstants from "@/customer/constants/router.constants";
import CustomerMainContent from "@/customer/views/MainContent.vue";
import CustomerBookingWrapper from "@/customer/views/BookingWrapper.vue";
import CustomerBooking from "@/customer/components/Booking.vue";
import CustomerBookingMenu from "@/customer/components/BookingMenu.vue";
import CustomerBookingMenuCategory from "@/customer/components/BookingMenuCategory.vue";
import CustomerBookingOption from "@/customer/components/BookingOption.vue";
import CustomerBookingCalendar from "@/customer/components/BookingCalendar.vue";
import PersonalInfo from "@/customer/components/PersonalInfo.vue";
import ChildBookingInfo from "@/customer/components/ChildBookingInfo.vue";
import ContentConfirmation from "@/customer/components/ContentConfirmation.vue";
import BookingSuccess from "@/customer/components/BookingSuccess.vue";
import BookingCancelSuccess from "@/customer/views/BookingCancelSuccess.vue";
import BookingHistory from "@/customer/components/BookingHistory.vue";
import CustomerInfos from "@/customer/components/CustomerInfos.vue";
import OtpForm from "@/customer/components/OtpForm.vue";

/**
 * 共通ユーティリティのインポート
 * - AuthUtil: 認証関連ユーティリティ
 * - RouterConstants: ルーター定数
 * - RouterUtils: ルーター関連ユーティリティ
 */
import AuthUtil from "@/common/utils/auth.util";
import RouterConstants from "@/clinic/constants/router.constants";
import RouterUtils from "@/common/utils/router.util";

/**
 * 外部スクリプトと定数のインポート
 * - useScriptTag: 外部スクリプトの動的読み込み
 * - CommonConstants: アプリケーション全体で使用する定数
 */
import { useScriptTag } from "@vueuse/core";
import CommonConstants from "@/common/constants/app.constants";

/** 予約プレビュー画面コンポーネント */
import CustomerBookingPreview from "@/customer/components/BookingPreviewOnly.vue";

/**
 * Cookieからアクセストークンを取得する関数
 * @returns {boolean} 認証状態（true: 認証済み、false: 未認証）
 */
const { get: getCookie } = useCookies();
const ifClientAuthenticated = (): boolean => {
  const token = getCookie("access_token");
  if (token && AuthUtil.checkAccessToken(token)) {
    // 認証済みの場合、Channel.ioのスクリプトを読み込み
    useScriptTag(`${CommonConstants.BASE_URL}/js/channel-io.js`);
    return true;
  }

  // 未認証の場合はサインイン画面にリダイレクト
  RouterUtils.goTo(RouterConstants.SIGN_IN_ROUTER_NAME);
  return false;
};

/**
 * ルート定義
 * クリニック側とカスタマー側の両方のルートを含む
 */
const routes: Array<RouteRecordRaw> = [
  // ルートパスのリダイレクト設定
  { path: "/", redirect: ClinicRouterConstants.SIGN_IN_ROUTER_PATH },

  // クリニック側の認証関連ルート
  {
    path: ClinicRouterConstants.SIGN_IN_ROUTER_PATH,
    name: ClinicRouterConstants.SIGN_IN_ROUTER_NAME,
    component: SignIn,
  },
  {
    path: ClinicRouterConstants.FORGOT_PASSWORD_ROUTER_PATH,
    name: ClinicRouterConstants.FORGOT_PASSWORD_ROUTER_NAME,
    component: ForgotPassword,
  },
  {
    path: ClinicRouterConstants.RESET_PASSWORD_ROUTER_PATH,
    name: ClinicRouterConstants.RESET_PASSWORD_ROUTER_NAME,
    component: ResetPassword,
  },
  {
    path: ClinicRouterConstants.CLINIC_ROOT_ROUTER_PATH,
    component: MainContent,
    children: [
      // ダッシュボード画面
      {
        path: ClinicRouterConstants.DASHBOARD_ROUTER_PATH,
        name: ClinicRouterConstants.DASHBOARD_ROUTER_NAME,
        component: Dashboard,
      },
      // プライバシーポリシー画面
      {
        path: ClinicRouterConstants.PRIVACY_POLICY_ROUTER_PATH,
        name: ClinicRouterConstants.PRIVACY_POLICY_ROUTER_NAME,
        component: PrivacyPolicy,
      },
      // 利用規約画面
      {
        path: ClinicRouterConstants.TERMS_OF_SERVICE_ROUTER_PATH,
        name: ClinicRouterConstants.TERMS_OF_SERVICE_ROUTER_NAME,
        component: TermsOfService,
      },
      // 会社概要画面
      {
        path: ClinicRouterConstants.COMPANY_PROFILE_ROUTER_PATH,
        name: ClinicRouterConstants.COMPANY_PROFILE_ROUTER_NAME,
        component: CompanyProfile,
      },
      // 予約管理画面
      {
        path: ClinicRouterConstants.BOOKING_ROUTER_ROUTER_PATH,
        name: ClinicRouterConstants.BOOKING_ROUTER_NAME,
        component: Booking,
      },
      // クリニック日常業務管理画面
      {
        path: ClinicRouterConstants.CLINIC_DAILY_ROUTINE_ROUTER_PATH,
        name: ClinicRouterConstants.CLINIC_DAILY_ROUTINE_ROUTER_NAME,
        component: ClinicRoutine,
      },
      // キャンペーン管理画面
      {
        path: ClinicRouterConstants.CAMPAIGN_ROUTER_PATH,
        name: ClinicRouterConstants.CAMPAIGN_ROUTER_NAME,
        component: Campaign,
      },
      // 新規キャンペーン作成画面
      {
        path: ClinicRouterConstants.CAMPAIGN_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.CAMPAIGN_NEW_ROUTER_NAME,
        component: CampaignNew,
      },
      // キャンペーン結果画面
      {
        path: ClinicRouterConstants.CAMPAIGN_RESULT_ROUTER_PATH,
        name: ClinicRouterConstants.CAMPAIGN_RESULT_ROUTER_NAME,
        component: CampaignResult,
      },
      // キャンペーンデータなし画面
      {
        path: ClinicRouterConstants.CAMPAIGN_NODATA_ROUTER_PATH,
        name: ClinicRouterConstants.CAMPAIGN_NODATA_ROUTER_NAME,
        component: CampaignNodata,
      },
      // お問い合わせ管理画面
      {
        path: ClinicRouterConstants.CONTACT_ROUTER_PATH,
        name: ClinicRouterConstants.CONTACT_ROUTER_NAME,
        component: Contact,
      },
      // 顧客管理画面
      {
        path: ClinicRouterConstants.CUSTOMER_ROUTER_PATH,
        name: ClinicRouterConstants.CUSTOMER_ROUTER_NAME,
        component: Customer,
      },
      // 新規顧客登録画面
      {
        path: ClinicRouterConstants.CUSTOMER_CREATE_ROUTER_PATH,
        name: ClinicRouterConstants.CUSTOMER_CREATE_ROUTER_NAME,
        component: CustomerNew,
      },
      // 顧客編集画面
      {
        path: ClinicRouterConstants.CUSTOMER_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.CUSTOMER_EDIT_ROUTER_NAME,
        component: CustomerEdit,
      },
      // 設定画面
      {
        path: ClinicRouterConstants.SETTING_ROUTER_ROUTER_PATH,
        name: ClinicRouterConstants.SETTING_ROUTER_NAME,
        component: Setting,
      },
      // セグメントワークフロー画面
      {
        path: ClinicRouterConstants.SEGMENT_WORKFLOW_ROUTER_PATH,
        name: ClinicRouterConstants.SEGMENT_WORKFLOW_ROUTER_NAME,
        component: SegmentWorkflow,
      },
      // リッチメニュー管理画面
      {
        path: ClinicRouterConstants.RICH_MENU_ROUTER_PATH,
        name: ClinicRouterConstants.RICH_MENU_ROUTER_NAME,
        component: RichMenu,
      },
      // リッチメニュー登録画面
      {
        path: ClinicRouterConstants.RICH_MENU_REGISTRATION_ROUTER_PATH,
        name: ClinicRouterConstants.RICH_MENU_REGISTRATION_ROUTER_NAME,
        component: RichMenuRegistration,
      },
      // リッチメニュー編集画面
      {
        path: ClinicRouterConstants.RICH_MENU_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.RICH_MENU_EDIT_ROUTER_NAME,
        component: RichMenuEdit,
      },
      // 新規メッセージ作成画面
      {
        path: ClinicRouterConstants.MESSAGE_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.MESSAGE_NEW_ROUTER_NAME,
        component: MessageNew,
      },
      // メッセージ編集画面
      {
        path: ClinicRouterConstants.MESSAGE_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.MESSAGE_EDIT_ROUTER_NAME,
        component: MessageEdit,
      },
      // メッセージ一覧画面
      {
        path: ClinicRouterConstants.MESSAGE_ROUTER_PATH,
        name: ClinicRouterConstants.MESSAGE_ROUTER_NAME,
        component: MessageList,
      },
      // ダイレクトメッセージ画面
      {
        path: ClinicRouterConstants.DIRECT_MESSAGE_ROUTER_PATH,
        name: ClinicRouterConstants.DIRECT_MESSAGE_ROUTER_NAME,
        component: DirectMessage,
      },
      // 新規スタッフ登録画面
      {
        path: ClinicRouterConstants.STAFF_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.STAFF_NEW_ROUTER_NAME,
        component: StaffNew,
      },
      // スタッフ一覧画面
      {
        path: ClinicRouterConstants.STAFF_LIST_ROUTER_PATH,
        name: ClinicRouterConstants.STAFF_LIST_ROUTER_NAME,
        component: StaffList,
      },
      // スタッフ編集画面
      {
        path: ClinicRouterConstants.STAFF_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.STAFF_EDIT_ROUTER_NAME,
        component: StaffEdit,
      },
      // スタッフシフト管理画面
      {
        path: ClinicRouterConstants.STAFF_SHIFT_ROUTER_PATH,
        name: ClinicRouterConstants.STAFF_SHIFT_ROUTER_NAME,
        component: ClinicSettingsStaffShift,
      },
      // メニュー登録画面
      {
        path: ClinicRouterConstants.MENU_REGISTRATION_ROUTER_PATH,
        name: ClinicRouterConstants.MENU_REGISTRATION_ROUTER_NAME,
        component: NewMedicalMenu,
      },
      // メニュー編集画面
      {
        path: ClinicRouterConstants.MENU_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.MENU_EDIT_ROUTER_NAME,
        component: EditingMedicalMenu,
      },
      // メニュー一覧画面
      {
        path: ClinicRouterConstants.MENU_ROUTER_PATH,
        name: ClinicRouterConstants.MENU_ROUTER_NAME,
        component: MedicalMenuList,
      },
      // 予約設定画面
      {
        path: ClinicRouterConstants.BOOKING_SETTING_ROUTER_PATH,
        name: ClinicRouterConstants.BOOKING_SETTING_ROUTER_NAME,
        component: BookingSettingsList,
      },
      // 医療オプション管理画面
      {
        path: ClinicRouterConstants.MEDICAL_OPTIONS_ROUTER_PATH,
        name: ClinicRouterConstants.MEDICAL_OPTIONS_ROUTER_NAME,
        component: MedicalOptions,
      },
      // 新規医療オプション作成画面
      {
        path: ClinicRouterConstants.MEDICAL_OPTIONS_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.MEDICAL_OPTIONS_NEW_ROUTER_NAME,
        component: MedicalOptionNew,
      },
      // 医療オプション編集画面
      {
        path: ClinicRouterConstants.MEDICAL_OPTIONS_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.MEDICAL_OPTIONS_EDIT_ROUTER_NAME,
        component: MedicalOptionEdit,
      },
      // 医療メニューカテゴリー管理画面
      {
        path: ClinicRouterConstants.MEDICAL_MENU_CATEGORY_ROUTER_PATH,
        name: ClinicRouterConstants.MEDICAL_MENU_CATEGORY_ROUTER_NAME,
        component: MedicalMenuCategories,
      },
      // ライブメッセージテンプレート管理画面
      {
        path: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_ROUTER_PATH,
        name: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_ROUTER_NAME,
        component: LiveMessageTemplates,
      },
      // 新規ライブメッセージテンプレート作成画面
      {
        path: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_NEW_ROUTER_NAME,
        component: LiveMessageTemplateNew,
      },
      // ライブメッセージテンプレート編集画面
      {
        path: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.LIVE_MESSAGE_TEMPLATE_EDIT_ROUTER_NAME,
        component: LiveMessageTemplateEdit,
      },
      // 顧客詳細画面
      {
        path: ClinicRouterConstants.CLIENT_DETAILS_ROUTER_PATH,
        name: ClinicRouterConstants.CLIENT_DETAILS_ROUTER_NAME,
        component: ClientDetailsList,
      },
      // 新規LINE通知メッセージ作成画面
      {
        path: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_NEW_ROUTER_PATH,
        name: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_NEW_ROUTER_NAME,
        component: LineNotificationMessageNew,
      },
      // LINE通知メッセージ編集画面
      {
        path: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_EDIT_ROUTER_PATH,
        name: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_EDIT_ROUTER_NAME,
        component: LineNotificationMessageEdit,
      },
      // LINE通知メッセージ管理画面
      {
        path: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_ROUTER_PATH,
        name: ClinicRouterConstants.LINE_NOTIFICATION_MESSAGE_ROUTER_NAME,
        component: LineNotificationMessage,
      },
      // サービスタグ管理画面
      {
        path: ClinicRouterConstants.SERVICE_TAGS_ROUTER_PATH,
        name: ClinicRouterConstants.SERVICE_TAGS_ROUTER_NAME,
        component: ServiceTag,
      },
      // セグメント用手動タグ管理画面
      {
        path: ClinicRouterConstants.SEGMENT_MANUAL_TAGS_ROUTER_PATH,
        name: ClinicRouterConstants.SEGMENT_MANUAL_TAGS_ROUTER_NAME,
        component: SegmentManualTags,
      },
      // 配信グループ管理画面
      {
        path: ClinicRouterConstants.DISTRIBUTION_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.DISTRIBUTION_GROUP_ROUTER_NAME,
        component: DistributionGroup,
      },
      // 新規配信グループ作成画面
      {
        path: ClinicRouterConstants.NEW_DISTRIBUTION_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.NEW_DISTRIBUTION_GROUPS_NAME,
        component: NewDistributionGroup,
      },
      // 配信グループ編集画面
      {
        path: ClinicRouterConstants.EDIT_DISTRIBUTION_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.EDIT_DISTRIBUTION_GROUP_ROUTER_NAME,
        component: EditDistributionGroup,
      },
      // 配信グループ複製画面
      {
        path: ClinicRouterConstants.DUPLICATE_DISTRIBUTION_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.DUPLICATE_DISTRIBUTION_GROUP_ROUTER_NAME,
        component: DuplicateDistributionGroup,
      },
      // 配信グループ下書き一覧画面
      {
        path: ClinicRouterConstants.DISTRIBUTION_GROUPS_DRAFTS_PATH,
        name: ClinicRouterConstants.DISTRIBUTION_GROUPS_DRAFTS_NAME,
        component: DistributionGroupDrafts,
      },
      // ターゲット顧客一覧画面
      {
        path: ClinicRouterConstants.TARGET_CUSTOMER_ROUTER_PATH,
        name: ClinicRouterConstants.TARGET_CUSTOMER_ROUTER_NAME,
        component: TargetCustomers,
      },
      // タグ付き配信サマリー画面
      {
        path: ClinicRouterConstants.TAGGED_DELIVERY_ROUTER_PATH,
        name: ClinicRouterConstants.TAGGED_DELIVERY_ROUTER_NAME,
        component: TaggedDelivery,
      },
      // 定期配信グループ管理画面
      {
        path: ClinicRouterConstants.DISTRIBUTION_ROUTINE_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.DISTRIBUTION_ROUTINE_GROUP_ROUTER_NAME,
        component: RoutineDistributionGroup,
      },
      // 新規定期配信グループ作成画面
      {
        path: ClinicRouterConstants.NEW_DISTRIBUTION_ROUTINE_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.NEW_DISTRIBUTION_ROUTINE_GROUP_ROUTER_NAME,
        component: NewRoutineDistributionGroup,
      },
      // 定期配信グループ編集画面
      {
        path: ClinicRouterConstants.EDIT_DISTRIBUTION_ROUTINE_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.EDIT_DISTRIBUTION_ROUTINE_GROUP_ROUTER_NAME,
        component: EditRoutineDistributionGroup,
      },
      // 定期配信グループ下書き一覧画面
      {
        path: ClinicRouterConstants.DISTRIBUTION_GROUPS_ROUTINE_DRAFTS_PATH,
        name: ClinicRouterConstants.DISTRIBUTION_GROUPS_ROUTINE_DRAFTS_NAME,
        component: DistributionRoutineGroupDrafts,
      },
      // 定期配信グループ複製画面
      {
        path: ClinicRouterConstants.DUPLICATE_DISTRIBUTION_ROUTINE_GROUP_ROUTER_PATH,
        name: ClinicRouterConstants.DUPLICATE_DISTRIBUTION_ROUTINE_GROUP_ROUTER_NAME,
        component: DuplicateRoutineDistributionGroup,
      },
      // 予約通知管理画面
      {
        path: ClinicRouterConstants.BOOKING_NOTIFICATIONS_ROUTER_PATH,
        name: ClinicRouterConstants.BOOKING_NOTIFICATIONS_ROUTE_NAME,
        component: BookingNotifications,
      },
      // スタッフ優先順位管理画面
      {
        path: ClinicRouterConstants.STAFF_PRIORITIES_ROUTER_PATH,
        name: ClinicRouterConstants.STAFF_PRIORITIES_ROUTER_NAME,
        component: StaffPriority,
      },
      // 部屋管理画面
      {
        path: ClinicRouterConstants.ROOMS_ROUTER_PATH,
        name: ClinicRouterConstants.ROOMS_ROUTE_NAME,
        component: Rooms,
      },
      // 重複顧客管理画面
      {
        path: ClinicRouterConstants.DUPLICATE_CUSTOMERS_ROUTER_PATH,
        name: ClinicRouterConstants.DUPLICATE_CUSTOMERS_ROUTE_NAME,
        component: DuplicateCustomer,
      },
    ],
    beforeEnter: ifClientAuthenticated,
  },
  // カスタマー側のルート
  {
    path: CustomerRouterConstants.CUSTOMER_ROOT_PATH,
    name: CustomerRouterConstants.CUSTOMER_ROUTER_NAME,
    component: CustomerMainContent,
    redirect: CustomerRouterConstants.BOOKING_MENU_CATEGORY_ROUTER_PATH,
    children: [
      // 予約履歴画面
      {
        path: CustomerRouterConstants.BOOKING_HISTORY_ROUTER_PATH,
        name: CustomerRouterConstants.BOOKING_HISTORY_ROUTER_NAME,
        component: BookingHistory,
      },
      // 顧客情報画面
      {
        path: CustomerRouterConstants.CUSTOMER_INFOS_ROUTER_PATH,
        name: CustomerRouterConstants.CUSTOMER_INFOS_ROUTER_NAME,
        component: CustomerInfos,
      },
      // 予約フロー画面
      {
        path: CustomerRouterConstants.BOOKING_ROUTER_PATH,
        component: CustomerBookingWrapper,
        children: [
          // 予約メニューカテゴリー画面へのリダイレクト
          {
            path: "",
            redirect: CustomerRouterConstants.BOOKING_MENU_CATEGORY_ROUTER_PATH,
          },
          // OTP認証画面
          {
            path: CustomerRouterConstants.OTP_FORM_PATH,
            name: CustomerRouterConstants.OTP_FORM_NAME,
            component: OtpForm,
          },
          // 予約画面
          {
            path: CustomerRouterConstants.BOOKING_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_ROUTER_NAME,
            component: CustomerBooking,
          },
          // 予約メニューカテゴリー選択画面
          {
            path: CustomerRouterConstants.BOOKING_MENU_CATEGORY_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_MENU_CATEGORY_ROUTER_NAME,
            component: CustomerBookingMenuCategory,
          },
          // 予約メニュー選択画面
          {
            path: CustomerRouterConstants.BOOKING_MENU_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_MENU_ROUTER_NAME,
            component: CustomerBookingMenu,
          },
          // 予約オプション選択画面
          {
            path: CustomerRouterConstants.BOOKING_OPTION_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_OPTION_ROUTER_NAME,
            component: CustomerBookingOption,
          },
          // 予約カレンダー画面
          {
            path: CustomerRouterConstants.BOOKING_CALENDAR_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_CALENDAR_ROUTER_NAME,
            component: CustomerBookingCalendar,
          },
          // 個人情報入力画面
          {
            path: CustomerRouterConstants.PERSONAL_INFO_ROUTER_PATH,
            name: CustomerRouterConstants.PERSONAL_INFO_ROUTER_NAME,
            component: PersonalInfo,
          },
          // 子供予約情報入力画面
          {
            path: CustomerRouterConstants.CHILD_BOOKING_INFO_ROUTER_PATH,
            name: CustomerRouterConstants.CHILD_BOOKING_INFO_ROUTER_NAME,
            component: ChildBookingInfo,
          },
          // 内容確認画面
          {
            path: CustomerRouterConstants.CONTENT_CONFIRMATION_ROUTER_PATH,
            name: CustomerRouterConstants.CONTENT_CONFIRMATION_ROUTER_NAME,
            component: ContentConfirmation,
          },
          // 予約完了画面
          {
            path: CustomerRouterConstants.BOOKING_SUCCESS_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_SUCCESS_ROUTER_NAME,
            component: BookingSuccess,
          },
          // 予約キャンセル完了画面
          {
            path: CustomerRouterConstants.BOOKING_CANCEL_SUCCESS_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_CANCEL_SUCCESS_ROUTER_NAME,
            component: BookingCancelSuccess,
          },
          // 予約プレビュー画面
          {
            path: CustomerRouterConstants.BOOKING_PREVIEW_ROUTER_PATH,
            name: CustomerRouterConstants.BOOKING_PREVIEW_ROUTER_NAME,
            component: CustomerBookingPreview,
          },
        ],
      },
    ],
  },
];

/**
 * Vue Routerインスタンスの作成
 * HTML5 History Modeを使用
 */
const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * sessionStorage管理のためのルーターガード
 * カレンダー画面から他の画面へ遷移する際にsessionStorageをクリアして
 * 画面遷移後の復帰時は当日の日付を表示するようにする
 */
router.beforeEach((to, from, next) => {
  // カレンダーページ（/clinic/bookings）から他のページへの遷移を検出
  const isLeavingCalendar = from.path === "/clinic/bookings" && to.path !== "/clinic/bookings";

  if (isLeavingCalendar) {
    // カレンダーから離れる時にsessionStorageをクリア
    sessionStorage.removeItem("booking-calendar-date");
  }

  next();
});

export default router;
